import { useEffect, useState } from "react";

/**
 * What a child looks at while the model works.
 *
 * The old version was one static word, which for an eight-second wait reads as
 * "nothing is happening" — and a child who thinks nothing is happening taps
 * again. So this does two things a spinner cannot: it shows the star being
 * assembled out of drifting dust, and it says which part of the work is
 * underway.
 *
 * The stages are the pipeline's real ones, in the real order — screen the
 * question, write the answer, build the check. The timings are measured
 * averages rather than progress reported by the server, so treat the line as an
 * honest description of what is happening, not a claim that a step has
 * finished. It never reaches a "done" state on its own: the last line holds
 * until the answer actually lands, so the wait can run long without the screen
 * ever having lied.
 */
const STAGES: { after: number; line: string }[] = [
  { after: 0, line: "Reading your question…" },
  { after: 1400, line: "Looking it up…" },
  { after: 4200, line: "Working out how to explain it…" },
  { after: 7600, line: "Putting your check together…" },
  { after: 12000, line: "Nearly there…" },
];

/**
 * Where each dust mote drifts in from, and in which of the sky's colours.
 * Spread at an odd interval so the ring never reads as a clock face, and
 * mixed rather than uniform so it looks like dust rather than a loading
 * spinner with extra steps.
 */
const MOTES: { angle: number; color: string }[] = [
  { angle: 0, color: "bg-cyan" },
  { angle: 41, color: "bg-lilac" },
  { angle: 79, color: "bg-gold" },
  { angle: 118, color: "bg-cyan" },
  { angle: 157, color: "bg-pink" },
  { angle: 196, color: "bg-lilac" },
  { angle: 238, color: "bg-cyan" },
  { angle: 277, color: "bg-gold" },
  { angle: 318, color: "bg-lilac" },
];

export function Thinking() {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timers = STAGES.slice(1).map((s, i) =>
      window.setTimeout(() => setStage(i + 1), s.after),
    );
    return () => timers.forEach(window.clearTimeout);
  }, []);

  return (
    <div className="rise py-10 text-center">
      <div className="relative mx-auto h-[132px] w-[132px]">
        {MOTES.map((mote, i) => (
          <span
            key={mote.angle}
            className={`mote absolute left-1/2 top-1/2 -ml-[4px] -mt-[4px] h-[8px] w-[8px] rounded-full ${mote.color}`}
            style={{
              // Custom property rather than a per-mote keyframe: nine of these
              // hand-written would be nine chances to get one wrong.
              ["--a" as string]: `${mote.angle}deg`,
              // Not evenly spaced across the cycle — evenly spaced looks
              // mechanical, and dust doesn't arrive on a schedule.
              animationDelay: `${(i * 0.29) % 2.6}s`,
            }}
          />
        ))}
        <div className="forming absolute inset-0 flex items-center justify-center text-[46px] text-gold">
          ✦
        </div>
      </div>

      {/*
        Announced politely so a child using VoiceOver hears the work progress
        too — the animation says "something is happening" only if you can see it.
      */}
      <p
        key={stage}
        className="stage-line disp mt-4 text-[20px] text-cream"
        aria-live="polite"
      >
        {STAGES[stage]!.line}
      </p>

      {/*
        Three pips for the pipeline's three real steps — screen, answer, build
        the check — not five for the five lines. "Nearly there" is the fourth
        line but not a fourth step, and lighting a pip for it would be inventing
        progress the server never reported.
      */}
      <div className="mt-3 flex items-center justify-center gap-2">
        {[0, 1, 3].map((threshold, i) => (
          <span
            key={i}
            className={`h-[7px] rounded-full transition-all duration-500 ${
              stage >= threshold ? "w-6 bg-gold" : "w-[7px] bg-cream/25"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
